/**
 * Select By Location — pick features out of one layer using another layer's
 * geometry.
 *
 * The sibling the Query tools were missing. Query selects by ATTRIBUTE, the
 * box-select tool selects by a shape you DRAW, and this one selects by a shape
 * that is already on the map: every pipe inside Ward 7, every building within
 * 50 m of the flood zone, every parcel a proposed road crosses.
 *
 * Results go out on the same selection channel every other tool uses, so they
 * are highlighted on the map and ticked in an open attribute table without
 * this panel knowing either of those exist. That is also what makes the
 * combine modes worth having: an attribute query followed by "select from
 * current selection" here is how the two tools are meant to be used together.
 */

import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { cfg, scopedLayers, type SelectionRow, type ToolContext } from './types';
import {
  EmptyHint,
  Field,
  FieldRow,
  Notice,
  ResultHeader,
  ResultRow,
  ScrollList,
  btnClass,
  inputClass,
  LayerSelect,
} from './shared';
import { centroidOf, labelOf, loadFeaturesDetailed, type LoadResult } from './featureLoad';
import { getLayerDisplayName } from '../../../layers/capabilities';
import { featureKey } from '../../../utils/featureKey';
import {
  RELATIONSHIPS,
  bboxUnion,
  degreesForMetres,
  padBbox,
  prepare,
  relate,
  scaleForLatitude,
  worthTesting,
  type PreparedGeometry,
  type SpatialRelationship,
} from '../../../utils/spatialRelate';

const UNIT_METRES: Record<string, number> = { m: 1, km: 1000, ft: 0.3048, mi: 1609.344 };

/**
 * How many features each side of the comparison may bring back.
 *
 * A ceiling, not a target. It exists because the work is quadratic in the
 * worst case and this runs in the browser; the panel says plainly when it is
 * reached rather than quietly answering a smaller question than was asked.
 */
const MAX_TARGET_FEATURES = 4000;
const MAX_SOURCE_FEATURES = 4000;

type CombineMode = 'new' | 'add' | 'remove' | 'subset';

const COMBINE_MODES: { id: CombineMode; label: string; help: string }[] = [
  { id: 'new', label: 'New selection', help: 'Replaces whatever is selected now.' },
  { id: 'add', label: 'Add to selection', help: 'Keeps the current selection and adds these.' },
  { id: 'remove', label: 'Remove from selection', help: 'Takes these out of the current selection.' },
  {
    id: 'subset',
    label: 'Select from selection',
    help: 'Narrows the current selection to those that also match.',
  },
];

interface Outcome {
  matched: number;
  combined: number;
  scanned: number;
  sources: number;
  cappedTarget: boolean;
  cappedSource: boolean;
  rows: SelectionRow[];
  /** The selecting geometry was narrowed to an existing on-map selection. */
  usedSourceSelection: boolean;
}

export function SelectByLocationPanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);

  const [targetId, setTargetId] = useState(allowed[0]?.id ?? '');
  const [sourceId, setSourceId] = useState(allowed[1]?.id ?? allowed[0]?.id ?? '');
  const [relationship, setRelationship] = useState<SpatialRelationship>(
    cfg<SpatialRelationship>(ctx.config, 'relationship', 'intersect'),
  );
  const [distance, setDistance] = useState(String(cfg<number>(ctx.config, 'distance', 100)));
  const [unit, setUnit] = useState(cfg<string>(ctx.config, 'unit', 'm'));
  const [combine, setCombine] = useState<CombineMode>('new');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const target = allowed.find((layer) => layer.id === targetId);
  const source = ctx.layers.find((layer) => layer.id === sourceId);
  const needsDistance = relationship === 'withinDistance';
  const help = RELATIONSHIPS.find((entry) => entry.id === relationship)?.help ?? '';

  /* The selecting layer is drawn from EVERY layer, not just the scoped ones.
     What a tool may select FROM is a permission an admin sets; what it may
     select BY is only a question of what is on the map, and narrowing it would
     rule out using a boundary layer nobody is meant to select out of. */
  const sourceChoices = ctx.layers;

  const sameLayer = Boolean(targetId) && targetId === sourceId;

  const run = async () => {
    if (!target || !source) return;

    const metres = Number(distance) * (UNIT_METRES[unit] ?? 1);
    if (needsDistance && (!Number.isFinite(metres) || metres <= 0)) {
      setError('Enter a search distance greater than zero.');
      return;
    }

    setError(null);
    setBusy(true);
    setOutcome(null);

    try {
      /* If the selecting layer already has features picked out on the map —
         one polygon out of Layer A's three, say — that pick IS the question
         being asked, and pulling the whole layer instead would silently
         answer a broader one. Only rows that belong to THIS layer count: the
         app-wide selection can hold picks from other layers too. */
      const sourceSelection = (ctx.getSelection?.() ?? []).filter(
        (row) => row.layerId === source.id && row.geometry,
      );

      /* The selecting layer first: its extent is what lets the target layer be
         requested narrowly instead of whole. */
      const sourceLoad: LoadResult =
        sourceSelection.length > 0
          ? {
              features: sourceSelection.map((row, index) => ({
                type: 'Feature',
                id: row.id ?? index,
                geometry: row.geometry as GeoJSON.Geometry,
                properties: row.properties ?? {},
              })),
              truncated: false,
            }
          : await loadFeaturesDetailed(source, ctx, { limit: MAX_SOURCE_FEATURES });
      const sourceFeatures = sourceLoad.features;
      if (sourceFeatures.length === 0) {
        setError(
          `${getLayerDisplayName(source)} returned no features to select by. If it is an image-only service, this tool cannot read its geometry.`,
        );
        return;
      }

      const prepared = sourceFeatures
        .map((feature) => prepare(feature.geometry))
        .filter((geometry) => geometry.positions.length > 0);

      if (prepared.length === 0) {
        setError(`${getLayerDisplayName(source)} has no geometry to select by.`);
        return;
      }

      // Containment is only meaningful against an area, and answering "0
      // features" for a point layer would look like a data problem.
      if (relationship === 'within' && !prepared.some((geometry) => geometry.polygons.length > 0)) {
        setError(
          `"Completely within" needs the selecting layer to be polygons. ${getLayerDisplayName(source)} is not an area layer — try Intersect or a search distance.`,
        );
        return;
      }

      const extent = bboxUnion(prepared.map((geometry) => geometry.bbox));
      const scaleX = scaleForLatitude(extent);
      const pad = needsDistance && extent ? degreesForMetres(metres, (extent.minY + extent.maxY) / 2) : null;
      const searchBox = extent && pad ? padBbox(extent, pad.x, pad.y) : extent;

      const targetLoad = await loadFeaturesDetailed(target, ctx, {
        limit: MAX_TARGET_FEATURES,
        bbox: searchBox,
      });
      const targetFeatures = targetLoad.features;

      if (relationship === 'contains') {
        const anyArea = targetFeatures.some(
          (feature) => prepare(feature.geometry).polygons.length > 0,
        );
        if (!anyArea) {
          setError(
            `"Completely contain" needs ${getLayerDisplayName(target)} to be polygons — only an area can enclose something.`,
          );
          return;
        }
      }

      const padX = pad?.x ?? 0;
      const padY = pad?.y ?? 0;

      /* One cheap rejection before the loop over sources: most targets are
         nowhere near the selecting layer, and this settles them in four
         comparisons instead of one per source feature. */
      const overallBox = searchBox;

      const hits: SelectionRow[] = [];
      targetFeatures.forEach((feature, index) => {
        const geometry = prepare(feature.geometry);
        if (geometry.positions.length === 0) return;
        if (overallBox && !boxesTouch(geometry, overallBox)) return;

        const matched = prepared.some(
          (sourceGeometry) =>
            worthTesting(geometry, sourceGeometry, relationship, padX, padY) &&
            relate(geometry, sourceGeometry, relationship, metres, scaleX),
        );
        if (!matched) return;

        hits.push({
          ...(feature.properties ?? {}),
          id: String(feature.id ?? index),
          key: featureKey(feature, target.id),
          layerId: target.id,
          properties: (feature.properties ?? {}) as Record<string, unknown>,
          geometry: feature.geometry ?? undefined,
        });
      });

      /* Combined BY KEY, never by position: the current selection and this
         result are separate reads of the service, so the same feature can
         arrive at a different index in each. */
      const previous = ctx.getSelection?.() ?? [];
      const combined = combineSelections(previous, hits, combine);

      setOutcome({
        matched: hits.length,
        combined: combined.length,
        scanned: targetFeatures.length,
        sources: prepared.length,
        cappedTarget: targetLoad.truncated,
        cappedSource: sourceLoad.truncated,
        rows: hits,
        usedSourceSelection: sourceSelection.length > 0,
      });

      ctx.onSelectionChange?.(combined);
    } catch (failure) {
      console.error('[selectByLocation] failed', failure);
      setError('Could not complete that query. One of the layers did not answer.');
    } finally {
      setBusy(false);
    }
  };

  const clearSelection = () => {
    setOutcome(null);
    setError(null);
    ctx.onSelectionChange?.([]);
  };

  return (
    <>
      <ToolPanelHeader
        title="Select by location"
        description="Select features using another layer's geometry."
        onClose={ctx.onClose}
      />

      {allowed.length === 0 ? (
        <EmptyHint>This map has no layers yet. Add one from the Layers panel.</EmptyHint>
      ) : (
        <>
          <Field label="Select features from">
            <LayerSelect layers={allowed} value={targetId} onChange={setTargetId} />
          </Field>

          <Field label="That">
            <select
              value={relationship}
              onChange={(event) => setRelationship(event.target.value as SpatialRelationship)}
              className={inputClass}
            >
              {RELATIONSHIPS.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="The features in">
            <LayerSelect layers={sourceChoices} value={sourceId} onChange={setSourceId} />
          </Field>

          {needsDistance && (
            <FieldRow>
              <Field label="Search distance">
                <input
                  type="number"
                  min={0}
                  value={distance}
                  onChange={(event) => setDistance(event.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Unit">
                <select
                  value={unit}
                  onChange={(event) => setUnit(event.target.value)}
                  className={inputClass}
                >
                  <option value="m">Metres</option>
                  <option value="km">Kilometres</option>
                  <option value="ft">Feet</option>
                  <option value="mi">Miles</option>
                </select>
              </Field>
            </FieldRow>
          )}

          <Field label="Result">
            <select
              value={combine}
              onChange={(event) => setCombine(event.target.value as CombineMode)}
              className={inputClass}
            >
              {COMBINE_MODES.map((mode) => (
                <option key={mode.id} value={mode.id}>
                  {mode.label}
                </option>
              ))}
            </select>
          </Field>

          <p className="text-[10.5px] leading-snug text-slate-400">
            {help} {COMBINE_MODES.find((mode) => mode.id === combine)?.help}
          </p>

          {sameLayer && (
            <Notice tone="warn">
              Both layers are the same, so every feature will match itself. Pick a different
              selecting layer.
            </Notice>
          )}

          <ToolActions>
            <button
              type="button"
              onClick={() => void run()}
              disabled={!target || !source || busy}
              className={`${btnClass} gl-btn--primary`}
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {busy ? 'Selecting…' : 'Apply'}
            </button>
            <button type="button" onClick={clearSelection} className={`${btnClass} gl-btn--secondary`}>
              Clear selection
            </button>
          </ToolActions>

          {error && <Notice tone="warn">{error}</Notice>}

          {outcome && <Results outcome={outcome} ctx={ctx} target={target} />}
        </>
      )}
    </>
  );
}

/** Does a prepared geometry's box touch this one? A last cheap rejection. */
function boxesTouch(
  geometry: PreparedGeometry,
  box: { minX: number; minY: number; maxX: number; maxY: number },
): boolean {
  const b = geometry.bbox;
  if (!b) return false;
  return b.minX <= box.maxX && b.maxX >= box.minX && b.minY <= box.maxY && b.maxY >= box.minY;
}

/** New / add / remove / subset, all matched on `key`. */
function combineSelections(
  previous: SelectionRow[],
  hits: SelectionRow[],
  mode: CombineMode,
): SelectionRow[] {
  if (mode === 'new') return hits;

  if (mode === 'add') {
    const seen = new Set(previous.map((row) => row.key));
    return [...previous, ...hits.filter((row) => !seen.has(row.key))];
  }

  const hitKeys = new Set(hits.map((row) => row.key));
  if (mode === 'remove') return previous.filter((row) => !hitKeys.has(row.key));
  return previous.filter((row) => hitKeys.has(row.key));
}

function Results({
  outcome,
  ctx,
  target,
}: {
  outcome: Outcome;
  ctx: ToolContext;
  target: { id: string } | undefined;
}) {
  const name = useMemo(
    () => (target ? getLayerDisplayName(ctx.layers.find((l) => l.id === target.id) as any) : ''),
    [ctx.layers, target],
  );

  const selectionNote = outcome.usedSourceSelection
    ? ` — narrowed to the ${outcome.sources.toLocaleString()} feature${
        outcome.sources === 1 ? '' : 's'
      } already selected in that layer, not its whole content.`
    : '';

  if (outcome.matched === 0) {
    return (
      <EmptyHint>
        Nothing matched. {outcome.scanned.toLocaleString()} feature
        {outcome.scanned === 1 ? '' : 's'} were tested against {outcome.sources.toLocaleString()}{' '}
        selecting feature{outcome.sources === 1 ? '' : 's'}
        {selectionNote}
      </EmptyHint>
    );
  }

  return (
    <>
      <Notice>
        {outcome.matched.toLocaleString()} feature{outcome.matched === 1 ? '' : 's'} matched
        {outcome.combined !== outcome.matched && (
          <> — {outcome.combined.toLocaleString()} selected after combining</>
        )}
        .{selectionNote}
      </Notice>

      {/* A cap that was reached changes what the answer MEANS, so it is stated
          rather than left for someone to notice in the count. */}
      {(outcome.cappedTarget || outcome.cappedSource) && (
        <Notice tone="warn">
          {outcome.cappedTarget
            ? 'The target layer returned only part of its features'
            : 'The selecting layer returned only part of its features'}
          {' '}&mdash; the service stopped at {outcome.cappedTarget
            ? outcome.scanned.toLocaleString()
            : outcome.sources.toLocaleString()}. Anything past that was never tested, so this
          count is a floor, not a total. Filter one of the layers, or zoom the question down, to be
          sure of a complete answer.
        </Notice>
      )}

      <div className="space-y-1">
        <ResultHeader label={name || 'Matched'} count={outcome.matched} />
        <ScrollList max={220}>
          {outcome.rows.slice(0, 50).map((row, index) => {
            const centre = centroidOf(row.geometry);
            return (
              <ResultRow
                key={row.key ?? index}
                title={labelOf(row, index)}
                detail={centre ? `${centre[1].toFixed(5)}, ${centre[0].toFixed(5)}` : undefined}
                onClick={
                  centre
                    ? () => ctx.onGoToLocation([centre[1], centre[0]], Math.max(ctx.mapZoom, 17))
                    : undefined
                }
              />
            );
          })}
        </ScrollList>
        {outcome.rows.length > 50 && (
          <p className="text-[10.5px] text-slate-400">
            Showing the first 50. All {outcome.matched.toLocaleString()} are selected on the map and
            in the attribute table.
          </p>
        )}
      </div>
    </>
  );
}

export default SelectByLocationPanel;
